import { useState } from "react";
import { C } from "@bd/content";
import { money, yearOf, type Company, type Product } from "@bd/sim";
import { Icon, Sheet } from "@bd/ui-kit";
import { me, setMyPlayer, useGame } from "../store";
import { ScoreBadge } from "./Reviews";
import { t, tp } from "../i18n";

type Open = { co: string; what: "poach" | "licence" | "copublish" | "slots" } | null;

/** Online: everything you can do with the cousins, one card each. Offers reach them as a choice next quarter. */
export function Cousins() {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const toast = useGame((s) => s.toast);
  const mine = me(game)!;
  const cousins = game.companies.filter((c) => c.id !== mine.id);
  const [open, setOpen] = useState<Open>(null);
  const year = yearOf(game.week);
  const voted = game.votes?.[String(year)]?.[mine.id];
  const ballot = cousins.flatMap((c) => c.products.filter((p) => p.launched && game.week - p.releaseWeek <= 52).map((p) => ({ c, p })));
  const send = async (c: Parameters<typeof cmd>[0], ok: string) => {
    const r = await cmd(c);
    toast(r.ok ? { title: ok, text: t("They'll see it next quarter."), tone: "good" } : { title: t("Not sent"), text: r.error, tone: "warn" });
    if (r.ok) setOpen(null);
  };
  const visit = (c: Company) => {
    setMyPlayer(c.player);
    set({ screen: null, visiting: c.id, selectedRoom: null, closeup: null, tool: null, placing: null });
  };
  return (
    <Sheet title={t("The cousins")} onClose={() => set({ screen: null })} testId="cousins" wide>
      <div className="stack">
        {ballot.length > 0 && (
          <div className="card stack" style={{ gap: 6 }} data-testid="crowd-vote">
            <strong className="row" style={{ gap: 6 }} title={t("Most votes wins the crowd award. You can't vote for your own.")}><Icon name="trophy" size={16} color="var(--gold)" />{t("Dicefall Con {year}: crowd vote", { year })}</strong>
            <div className="row cousin-ballot" style={{ flexWrap: "wrap", gap: 6, maxHeight: 168, overflowY: "auto" }}>
              {ballot.map(({ c, p }) => (
                <button key={p.id} className={`btn small ${voted === p.marketId ? "primary" : ""}`} title={`${p.design.name} · ${c.name}`} onClick={() => void send({ type: "crowdVote", companyId: c.id, productId: p.id }, t("You voted for {game}", { game: p.design.name }))} data-testid={`vote-${p.id}`}>
                  {voted === p.marketId && <Icon name="check" size={14} />}<span className="ellipsis">{p.design.name}</span><span className="seat-dot" aria-label={c.name}>{c.name.slice(0, 1).toUpperCase()}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="grid2">
          {cousins.map((c) => (
            <CousinCard key={c.id} c={c} mine={mine} open={open?.co === c.id ? open.what : null} setOpen={(what) => setOpen(what ? { co: c.id, what } : null)} send={send} visit={() => visit(c)} />
          ))}
        </div>
      </div>
    </Sheet>
  );
}

function CousinCard({ c, mine, open, setOpen, send, visit }: {
  c: Company; mine: Company; open: NonNullable<Open>["what"] | null; setOpen: (w: NonNullable<Open>["what"] | null) => void;
  send: (c: never, ok: string) => Promise<void>; visit: () => void;
}) {
  const go = send as unknown as (cmd: object, ok: string) => Promise<void>;
  const released = c.products.filter((p) => p.launched);
  const best = [...released].sort((a, b) => b.score - a.score)[0] as Product | undefined;
  const hits = released.filter((p) => p.score >= 7.5).sort((a, b) => b.score - a.score).slice(0, 6);
  const seniors = c.staff.filter((s) => !s.founder && s.level >= 2);
  const hasShop = mine.rooms.some((r) => r.type === "print_shop");
  const projects = mine.projects.filter((p) => !p.partner);
  const [offer, setOffer] = useState<Record<string, number>>({});
  const [royalty, setRoyalty] = useState(0.1);
  const [share, setShare] = useState(0.3);
  const [slots, setSlots] = useState({ copies: 2000, price: 1.5 });
  const tab = (w: NonNullable<Open>["what"], label: string, why: string | null, help: string) => (
    <button className={`btn small ${open === w ? "primary" : ""}`} disabled={!!why} title={why ?? help} onClick={() => setOpen(open === w ? null : w)} data-testid={`cousin-${w}`}>{label}</button>
  );
  const whyNot = [
    seniors.length === 0 ? t("Nobody senior enough to poach yet") : null,
    hits.length === 0 ? t("No hits (7.5+) to license yet") : null,
    projects.length === 0 ? t("Start a game first: you share one in progress") : null,
    !hasShop ? t("Needs your own print shop (a studio room)") : null,
  ];
  return (
    <div className="card stack" style={{ gap: 8 }} data-testid={`cousin-${c.player}`}>
      <div className="spread">
        <strong className="ellipsis" title={t(C.level[c.level].name)}>{c.name}</strong>
        <button className="btn small" onClick={visit} title={t("Visit their studio")} data-testid="visit"><Icon name="zoom" size={14} />{t("Visit")}</button>
      </div>
      <span className="ichips">
        <span className="ichip" title={tp(c.staff.length, "{n} person", "{n} people")}><Icon name="staff" size={14} />{c.staff.length}</span>
        <span className="ichip" title={tp(released.length, "{n} game", "{n} games")}><Icon name="box" size={14} />{released.length}</span>
        {best && <span className="ichip" title={t("best: {game} ({score})", { game: best.design.name, score: best.score.toFixed(1) })}><Icon name="star" size={14} color="var(--gold)" />{best.score.toFixed(1)}</span>}
      </span>
      <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
        {tab("poach", t("Poach"), whyNot[0], t("Offer at least 20% over their pay. {company} can match it once.", { company: c.name }))}
        {tab("licence", t("License"), whyNot[1], t("Your next game in that theme is set in their world: some hype from the hit, and they get a share of sales."))}
        {tab("copublish", t("Co-publish"), whyNot[2], t("They pay that share of the first print run and get that share of every sale."))}
        {tab("slots", t("Print slots"), whyNot[3], t("They can print at your shop for a year and pay you per copy."))}
      </div>

      {open === "poach" && (
        <div className="stack" style={{ gap: 6 }}>
          {seniors.map((s) => {
            const o = offer[s.id] ?? Math.round(s.salary * 1.3);
            return (
              <div key={s.id} className="spread" style={{ gap: 8 }}>
                <span className="ichips" style={{ minWidth: 0 }}><strong>{s.name}</strong><span className="ichip plain muted" title={`${t(C.role[s.role].name)} · ${t("level {n}", { n: s.level })}`}>{t(C.role[s.role].name)} {s.level}</span><span className="ichip plain muted" title={t("{money}/wk", { money: money(s.salary) })}><Icon name="coin" size={13} color="var(--gold)" />{t("{money}/wk", { money: money(s.salary) })}</span></span>
                <span className="row" style={{ gap: 6 }}>
                  <input type="number" value={o} min={Math.ceil(s.salary * 1.2)} step={10} onChange={(e) => setOffer({ ...offer, [s.id]: Number(e.target.value) })} style={{ width: 90 }} aria-label={t("Weekly offer for {name}", { name: s.name })} />
                  <button className="btn small primary" onClick={() => void go({ type: "cousinPoach", companyId: c.id, staffId: s.id, offer: o }, t("Offer sent to {name}", { name: s.name }))} data-testid={`poach-${s.id}`}>{t("Offer")}</button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {open === "licence" && (
        <div className="stack" style={{ gap: 6 }}>
          <label className="spread"><span>{t("Royalty")}</span>
            <select value={royalty} onChange={(e) => setRoyalty(Number(e.target.value))}>{[0.05, 0.1, 0.15, 0.2, 0.25].map((r) => <option key={r} value={r}>{t("{n}% of sales", { n: Math.round(r * 100) })}</option>)}</select>
          </label>
          {hits.map((p) => (
            <div key={p.id} className="spread">
              <span className="row" style={{ gap: 8 }}><ScoreBadge score={p.score} /> {p.design.name} · {t(C.theme[p.design.theme].name)}</span>
              <button className="btn small primary" onClick={() => void go({ type: "licenceCousin", companyId: c.id, productId: p.id, royalty }, t("Licence request sent: {game}", { game: p.design.name }))} data-testid={`licence-${p.id}`}>{t("Ask")}</button>
            </div>
          ))}
        </div>
      )}

      {open === "copublish" && (
        <div className="stack" style={{ gap: 6 }}>
          <label className="spread"><span>{t("Their share")}</span>
            <select value={share} onChange={(e) => setShare(Number(e.target.value))}>{[0.2, 0.3, 0.4, 0.5].map((r) => <option key={r} value={r}>{Math.round(r * 100)}%</option>)}</select>
          </label>
          {projects.map((p) => (
            <div key={p.id} className="spread">
              <span>{p.design.name}</span>
              <button className="btn small primary" onClick={() => void go({ type: "coPublish", companyId: c.id, projectId: p.id, share }, t("Offer sent: {game}", { game: p.design.name }))} data-testid={`copublish-${p.id}`}>{t("Offer")}</button>
            </div>
          ))}
        </div>
      )}

      {open === "slots" && (
        <div className="stack" style={{ gap: 6 }}>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            <label className="row" style={{ gap: 4 }}>{t("Copies")} <input type="number" value={slots.copies} min={500} step={500} onChange={(e) => setSlots({ ...slots, copies: Number(e.target.value) })} style={{ width: 90 }} /></label>
            <label className="row" style={{ gap: 4 }}>{t("Price each")} <input type="number" value={slots.price} min={0.1} step={0.1} onChange={(e) => setSlots({ ...slots, price: Number(e.target.value) })} style={{ width: 80 }} /></label>
            <button className="btn small primary" onClick={() => void go({ type: "sellPrintSlots", companyId: c.id, copies: slots.copies, unitPrice: slots.price }, t("Print slots offered"))} data-testid="offer-slots">{t("Offer")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
